import { useEffect, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { useSocketContext } from "./index";
import { Button, message, notification } from "antd";
import {
  CopyOutlined,
  ExclamationCircleOutlined,
  ClusterOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useCompany from "../Hooks/company";
import { useNotificationSounds, SoundKey } from "../Hooks/useNotificationSounds";
import {
  playNotificationSound,
  preloadNotificationSounds,
} from "../Utilis/playNotificationSound";
import { tableQueryKeys } from "../Hooks/configration/tableQueryKeys";
import { useNotificationsStore } from "./notificationsStore";
import { debugLog, debugWarn } from "../Utilis/debug";
import { getOrdersCustomizations } from "../Utilis/customizations";
import { PERMISSIONS } from "../Utilis/permissions";

// Orange accent for missing-order alerts — same value the notification center
// uses for the "missing" kind, so toast and panel row match.
const MISSING_ORDER_COLOR = "#fd7e14";
const ORDER_FULFILLMENT_COLOR = "#d46b08";

/** CN-2382: SLA alerts close on their own; one scheduler run can raise many at once. */
const SLA_TOAST_SECONDS = 10;

/** Flatten permission titles from auth/me-style userInfo (roles and/or top-level list). */
const getUserPermissionTitles = (userInfo: any): Set<string> => {
  const titles = new Set<string>();
  const direct = userInfo?.permissions;
  if (Array.isArray(direct)) {
    for (const p of direct) {
      if (p?.title) titles.add(String(p.title));
    }
  }
  const roles = userInfo?.roles;
  if (Array.isArray(roles)) {
    for (const role of roles) {
      if (!Array.isArray(role?.permissions)) continue;
      for (const p of role.permissions) {
        if (p?.title) titles.add(String(p.title));
      }
    }
  }
  return titles;
};

const showBrowserNotification = (title: string, body: string) => {
  if (typeof window === "undefined") return;
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    new Notification(title, { body });
  } catch {
    // ignore — Notification ctor can throw in iframes / safari quirks
  }
};

export const NotificationsListener = () => {
  const { socket } = useSocketContext();
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const { company } = useCompany();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { add: addNotification } = useNotificationsStore();
  const { data: sounds, error: soundsError } = useNotificationSounds();

  useEffect(() => {
    if (soundsError) {
      debugWarn("[NotificationsListener] notification-sounds request failed:", soundsError);
      return;
    }
    if (!sounds) return;
    debugLog("[NotificationsListener] sounds map:", sounds);
    preloadNotificationSounds(
      Object.values(sounds).map((entry: any) => entry?.sound_url)
    );
  }, [sounds, soundsError]);

  useEffect(() => {
    if (!socket || !userInfo || !company) {
      debugLog("[NotificationsListener] Skipped – socket:", !!socket, "userInfo:", !!userInfo, "company:", !!company);
      return;
    }

    debugLog("[NotificationsListener] Registering listeners. socket.id:", (socket as any)?.id);

    const userType = userInfo.type;
    const userStoreIds: number[] =
      (userInfo.stores || []).map((s: any) => Number(s.id)) || [];
    const permissionTitles = getUserPermissionTitles(userInfo);

    const normalizedUserType = String(userType ?? "").toLowerCase();
    const isAdminLike =
      userType === 0 ||
      userType === 1 ||
      normalizedUserType === "0" ||
      normalizedUserType === "1" ||
      normalizedUserType === "admin" ||
      normalizedUserType === "super_admin" ||
      normalizedUserType === "superadmin" ||
      !!userInfo?.isAdmin;

    // Gates ticket/activity toasts, sounds, and notification-center rows.
    // Controlled solely by activity_notification (no admin bypass) so toggling
    // the permission always matches the expected popup behavior.
    const canReceiveActivityNotifications = permissionTitles.has(
      PERMISSIONS.ACTIVITY.NOTIFICATION
    );

    const currentCompanyId =
      company?.company_information?.id ??
      company?.company_information?.company_id ??
      company?.company_id ??
      company?.id ??
      userInfo?.company_id;

    debugLog("[NotificationsListener] userType:", userType, "userStoreIds:", userStoreIds, "currentCompanyId:", currentCompanyId);

    const canSeeForStoreAndCompany = (
      storeId: number | string | null | undefined,
      companyId: number | string | null | undefined
    ) => {
      debugLog("[NotificationsListener] canSee check — payload company:", companyId, "my company:", currentCompanyId, "payload store:", storeId, "my stores:", userStoreIds, "userType:", userType);

      // Company gate is fail-closed: the socket server broadcasts every company's
      // events to every client, so company A must never see company B's events.
      if (!currentCompanyId) {
        debugLog("[NotificationsListener] BLOCKED: current company id unresolved");
        return false;
      }
      if (!companyId) {
        debugLog("[NotificationsListener] BLOCKED: payload has no company_id");
        return false;
      }
      if (Number(companyId) !== Number(currentCompanyId)) {
        debugLog("[NotificationsListener] BLOCKED: company mismatch", Number(companyId), "!==", Number(currentCompanyId));
        return false;
      }

      // Anyone who can create orders (Agents included) should get company-wide
      // order toasts — not only Admin. Store scoping only applies to users
      // without order_create.
      const canCreateOrders =
        isAdminLike || permissionTitles.has(PERMISSIONS.ORDER.CREATE);
      if (canCreateOrders) return true;

      if (!storeId) return true;

      const numericStore = Number(storeId);
      if (!numericStore || userStoreIds.length === 0) return false;

      return userStoreIds.includes(numericStore);
    };

    const normalizeOrderEvent = (arg1: any, arg2?: any, arg3?: any) => {
      // Supports both formats:
      // 1) (store_id, order_id, company_id)
      // 2) ({ store_id, order_id, company_id, ... })
      if (arg1 && typeof arg1 === "object") {
        return {
          store_id: arg1.store_id ?? arg1.storeId ?? arg1.store?.id,
          order_id: arg1.order_id ?? arg1.orderId ?? arg1.id,
          company_id: arg1.company_id ?? arg1.companyId ?? arg1.company?.id,
        };
      }
      return {
        store_id: arg1,
        order_id: arg2,
        company_id: arg3,
      };
    };

    // The backend's `url` field points at the old blade dashboard, so it is
    // deliberately not read here — activity notifications always route in-app.
    const normalizeActivityEvent = (arg1: any, arg2?: any, arg3?: any) => {
      // Supports:
      // 1) single object payload
      // 2) nested payloads (activity/data/message)
      // 3) positional fallback
      const raw =
        arg1 && typeof arg1 === "object"
          ? arg1.activity ?? arg1.data ?? arg1.message ?? arg1
          : null;

      if (raw) {
        return {
          activity_id:
            raw.activity_id ?? raw.activityId ?? raw.id ?? raw.activity?.id,
          company_id:
            raw.company_id ?? raw.companyId ?? raw.company?.id ?? raw.company,
          store_id: raw.store_id ?? raw.storeId ?? raw.store?.id ?? raw.store,
          type_id: raw.type_id ?? raw.typeId ?? raw.type?.id,
          replied_by: raw.replied_by ?? raw.repliedBy ?? raw.user_name,
          created_by: raw.created_by ?? raw.createdBy ?? raw.user_name,
          changed_by: raw.changed_by ?? raw.changedBy ?? raw.user_name,
          customer_name: raw.customer_name ?? raw.customerName,
        };
      }

      return {
        activity_id: arg1,
        company_id: arg2,
        store_id: arg3,
        type_id: undefined,
        replied_by: undefined,
        created_by: undefined,
        changed_by: undefined,
        customer_name: undefined,
      };
    };

    const openActivity = (activityId: any) => {
      history.push(`/all-activities?activityId=${activityId}`);
    };

    const SELECTABLE_ID_STYLE: CSSProperties = {
      userSelect: "text",
      cursor: "text",
      fontWeight: 600,
    };

    const copyActivityId = async (
      event: MouseEvent,
      activityId: string | number
    ) => {
      event.stopPropagation();
      try {
        await navigator.clipboard.writeText(String(activityId));
        message.success(
          t("notifications.center.idCopied", "ID copied: {{id}}", {
            id: activityId,
          })
        );
      } catch {
        message.error(
          t("notifications.center.idCopyFailed", "Failed to copy ID")
        );
      }
    };

    // Activity toasts intentionally avoid a notice-level onClick so the ticket
    // number stays selectable. Navigation and copy are explicit actions below.
    const activityToastDescription = (
      activityId: string | number,
      beforeId: string,
      afterId: string,
      extraLine?: ReactNode
    ) => (
      <div
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div>
          {beforeId}
          <span style={SELECTABLE_ID_STYLE}>#{activityId}</span>
          {afterId}
        </div>
        {extraLine ? <div style={{ marginTop: 4 }}>{extraLine}</div> : null}
        <div
          style={{
            marginTop: 8,
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto" }}
            onClick={() => openActivity(activityId)}
          >
            {t("notifications.openTicket", "Open ticket")}
          </Button>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={(event) => copyActivityId(event, activityId)}
          >
            {t("notifications.copyId", "Copy ID")}
          </Button>
        </div>
      </div>
    );

    const playFor = (key: SoundKey) => {
      const url = sounds?.[key]?.sound_url;
      debugLog("[NotificationsListener] playFor:", key, "url:", url);
      playNotificationSound(url);
    };

    const refetchOrders = () => {
      const ordersCustomizations = getOrdersCustomizations(company);
      const listCustomizations = ordersCustomizations?.list;
      // Absent list slice → keep legacy auto-refresh. When present, honor live_refresh.
      const liveRefresh = listCustomizations
        ? !!listCustomizations.features?.live_refresh
        : true;
      if (!liveRefresh) return;
      queryClient.invalidateQueries({ queryKey: [tableQueryKeys.orders] });
    };

    const refetchActivities = () => {
      queryClient.invalidateQueries({ queryKey: [tableQueryKeys.activities] });
      queryClient.invalidateQueries({ queryKey: [tableQueryKeys.inquiry] });
      queryClient.invalidateQueries({ queryKey: [tableQueryKeys.complaint] });
    };

    const handleNewOrder = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] newOrder raw:", arg1, arg2, arg3);
      const { store_id, order_id, company_id } = normalizeOrderEvent(
        arg1,
        arg2,
        arg3
      );
      debugLog("[NotificationsListener] newOrder normalized:", { store_id, order_id, company_id });
      if (!order_id) { debugLog("[NotificationsListener] newOrder: no order_id, skipping"); return; }
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchOrders();

      const title = t("notifications.newOrder.title", "New order");
      const body = t("notifications.newOrder.body", "Store has new order: #{{id}}", { id: order_id });
      notification.success({
        message: title,
        description: body,
        duration: 0,
        onClick: () => {
          window.location.href = `/orders/show/${order_id}`;
        },
      });
      addNotification({ kind: "order", title, body, path: `/orders/show/${order_id}` });
      playFor("new_order");
      showBrowserNotification(title, body);
    };

    const handleOrderFulfillment = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] orderFulfillment raw:", arg1, arg2, arg3);
      const { store_id, order_id, company_id } = normalizeOrderEvent(
        arg1,
        arg2,
        arg3
      );
      if (!order_id) return;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchOrders();
      queryClient.invalidateQueries({ queryKey: ["order-fulfilments"] });

      const title = t(
        "notifications.orderFulfillment.title",
        "Order needs fulfilment"
      );
      const body = t(
        "notifications.orderFulfillment.body",
        "Order #{{id}} needs multi-warehouse fulfilment action",
        { id: order_id }
      );
      notification.open({
        message: title,
        description: body,
        duration: 0,
        icon: <ClusterOutlined style={{ color: ORDER_FULFILLMENT_COLOR }} />,
        style: { borderInlineStart: `4px solid ${ORDER_FULFILLMENT_COLOR}` },
        onClick: () => {
          window.location.href = `/orders/fulfilment`;
        },
      });
      addNotification({
        kind: "order_fulfillment",
        title,
        body,
        path: `/orders/fulfilment`,
      });
      playFor("new_order");
      showBrowserNotification(title, body);
    };

    const handleOrderMissing = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] orderMissing raw:", arg1, arg2, arg3);
      const { store_id, order_id, company_id } = normalizeOrderEvent(
        arg1,
        arg2,
        arg3
      );
      debugLog("[NotificationsListener] orderMissing normalized:", { store_id, order_id, company_id });
      if (!order_id) { debugLog("[NotificationsListener] orderMissing: no order_id, skipping"); return; }
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchOrders();

      const title = t("notifications.orderMissing.title", "Missing order");
      const body = t("notifications.orderMissing.body", "Order #{{id}} is missing", { id: order_id });
      // notification.open + explicit icon: antd has no orange preset, and
      // `warning` renders yellow (#faad14), not the orange this event needs.
      notification.open({
        message: title,
        description: body,
        duration: 0,
        icon: <ExclamationCircleOutlined style={{ color: MISSING_ORDER_COLOR }} />,
        style: { borderInlineStart: `4px solid ${MISSING_ORDER_COLOR}` },
        onClick: () => {
          window.location.href = `/orders/show/${order_id}`;
        },
      });
      addNotification({ kind: "missing", title, body, path: `/orders/show/${order_id}` });
      playFor("status_changed");
      showBrowserNotification(title, body);
    };

    const handleChangeOrderPay = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] changeOrderPay raw:", arg1, arg2, arg3);
      const { store_id, order_id, company_id } = normalizeOrderEvent(
        arg1,
        arg2,
        arg3
      );
      if (!order_id) return;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchOrders();

      const title = t("notifications.changeOrderPay.title", "Change order payment");
      const body = t("notifications.changeOrderPay.body", "Payment changed in order: #{{id}}", { id: order_id });
      notification.success({
        message: title,
        description: body,
        duration: 0,
        onClick: () => {
          window.location.href = `/orders/show/${order_id}`;
        },
      });
      addNotification({ kind: "warning", title, body, path: `/orders/show/${order_id}` });
      playFor("status_changed");
      showBrowserNotification(title, body);
    };

    const handleCancelOrder = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] cancelOrder raw:", arg1, arg2, arg3);
      const { store_id, order_id, company_id } = normalizeOrderEvent(
        arg1,
        arg2,
        arg3
      );
      if (!order_id) return;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchOrders();

      const title = t("notifications.cancelOrder.title", "Cancel order");
      const body = t("notifications.cancelOrder.body", "Order #{{id}} has been canceled", { id: order_id });
      notification.error({
        message: title,
        description: body,
        duration: 0,
        onClick: () => {
          window.location.href = `/orders/show/${order_id}`;
        },
      });
      addNotification({ kind: "error", title, body, path: `/orders/show/${order_id}` });
      playFor("status_changed");
      showBrowserNotification(title, body);
    };

    const handleReturnOrder = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] returnOrder raw:", arg1, arg2, arg3);
      const { store_id, order_id, company_id } = normalizeOrderEvent(
        arg1,
        arg2,
        arg3
      );
      if (!order_id) return;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchOrders();

      const title = t("notifications.returnOrder.title", "Returned order");
      const body = t("notifications.returnOrder.body", "Order #{{id}} has been returned", { id: order_id });
      notification.error({
        message: title,
        description: body,
        duration: 0,
        onClick: () => {
          window.location.href = `/orders/show/${order_id}`;
        },
      });
      addNotification({ kind: "error", title, body, path: `/orders/show/${order_id}` });
      playFor("status_changed");
      showBrowserNotification(title, body);
    };

    const typeNameFor = (typeId: any): string => {
      const n = Number(typeId);
      if (n === 0) return t("notifications.activityType.activity", "Activity");
      if (n === 1) return t("notifications.activityType.inquiry", "Inquiry");
      return t("notifications.activityType.complaint", "Complaint");
    };

    const soundKeyForActivityType = (typeId: any): SoundKey => {
      const n = Number(typeId);
      if (n === 0) return "new_activity";
      if (n === 1) return "new_inquiry";
      return "new_complaint";
    };

    const handleNewActivity = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] newActivity raw:", arg1, arg2, arg3);
      const data = normalizeActivityEvent(arg1, arg2, arg3);
      debugLog("[NotificationsListener] newActivity normalized:", data);
      if (!data.activity_id) { debugLog("[NotificationsListener] newActivity: no activity_id, skipping"); return; }
      const { company_id, store_id } = data;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      // Keep activity lists fresh even when the user cannot receive popups.
      refetchActivities();

      if (!canReceiveActivityNotifications) {
        debugLog(
          "[NotificationsListener] newActivity: missing activity_notification, skipping popup"
        );
        return;
      }

      const typeName = typeNameFor(data.type_id);
      const title = t("notifications.newActivity.title", "New {{type}}", { type: typeName });
      const createdBy =
        data.created_by || t("notifications.unknown", "Unknown");
      const description = t(
        "notifications.newActivity.body",
        "New {{type}} #{{id}} created by {{by}}",
        { type: typeName, id: data.activity_id, by: createdBy }
      );
      const customerLine = data.customer_name
        ? `${t("notifications.customer", "Customer")}: ${data.customer_name}`
        : undefined;
      const notificationBody = customerLine
        ? `${description}\n${customerLine}`
        : description;

      notification.success({
        message: title,
        description: activityToastDescription(
          data.activity_id,
          t("notifications.newActivity.bodyBeforeId", "New {{type}} ", {
            type: typeName,
          }),
          t("notifications.newActivity.bodyAfterId", " created by {{by}}", {
            by: createdBy,
          }),
          customerLine
        ),
        duration: 0,
      });
      addNotification({
        kind: "activity",
        title,
        body: notificationBody,
        path: `/all-activities?activityId=${data.activity_id}`,
        copyId: data.activity_id,
      });
      playFor(soundKeyForActivityType(data.type_id));
      showBrowserNotification(title, notificationBody);
    };

    const handleActivityReply = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] activityReply raw:", arg1, arg2, arg3);
      const data = normalizeActivityEvent(arg1, arg2, arg3);
      debugLog("[NotificationsListener] activityReply normalized:", data);
      if (!data.activity_id) { debugLog("[NotificationsListener] activityReply: no activity_id, skipping"); return; }
      const { company_id, store_id } = data;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchActivities();

      if (!canReceiveActivityNotifications) {
        debugLog(
          "[NotificationsListener] activityReply: missing activity_notification, skipping popup"
        );
        return;
      }

      const typeName = typeNameFor(data.type_id);
      const title = t("notifications.activityReply.title", "New Reply");
      const repliedBy =
        data.replied_by || t("notifications.unknown", "Unknown");
      const description = t(
        "notifications.activityReply.body",
        "New reply on {{type}} #{{id}} by {{by}}",
        { type: typeName, id: data.activity_id, by: repliedBy }
      );

      notification.info({
        message: title,
        description: activityToastDescription(
          data.activity_id,
          t("notifications.activityReply.bodyBeforeId", "New reply on {{type}} ", {
            type: typeName,
          }),
          t("notifications.activityReply.bodyAfterId", " by {{by}}", {
            by: repliedBy,
          })
        ),
        duration: 0,
      });
      addNotification({
        kind: "reply",
        title,
        body: description,
        path: `/all-activities?activityId=${data.activity_id}`,
        copyId: data.activity_id,
      });
      playFor("activity_reply");
      showBrowserNotification(title, description);
    };

    const handleActivityStatusChanged = (arg1: any, arg2?: any, arg3?: any) => {
      debugLog("[NotificationsListener] activityStatusChanged raw:", arg1, arg2, arg3);
      const data = normalizeActivityEvent(arg1, arg2, arg3);
      debugLog("[NotificationsListener] activityStatusChanged normalized:", data);
      if (!data.activity_id) { debugLog("[NotificationsListener] activityStatusChanged: no activity_id, skipping"); return; }
      const { company_id, store_id } = data;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchActivities();

      if (!canReceiveActivityNotifications) {
        debugLog(
          "[NotificationsListener] activityStatusChanged: missing activity_notification, skipping popup"
        );
        return;
      }

      const typeName = typeNameFor(data.type_id);
      const title = t("notifications.statusChanged.title", "Status Changed");
      const changedBy =
        data.changed_by || t("notifications.unknown", "Unknown");
      const description = t(
        "notifications.statusChanged.body",
        "{{type}} #{{id}} status changed by {{by}}",
        { type: typeName, id: data.activity_id, by: changedBy }
      );

      notification.warning({
        message: title,
        description: activityToastDescription(
          data.activity_id,
          t("notifications.statusChanged.bodyBeforeId", "{{type}} ", {
            type: typeName,
          }),
          t("notifications.statusChanged.bodyAfterId", " status changed by {{by}}", {
            by: changedBy,
          })
        ),
        duration: 0,
      });
      addNotification({
        kind: "status",
        title,
        body: description,
        path: `/all-activities?activityId=${data.activity_id}`,
        copyId: data.activity_id,
      });
      playFor("status_changed");
      showBrowserNotification(title, description);
    };

    // CN-2382: ticket running out of / past its closing time (SLA).
    // Emitted by the backend scheduler (notify:activity-sla), not by a user.
    const handleActivitySlaAlert = (payload: any) => {
      debugLog("[NotificationsListener] activitySlaAlert raw:", payload);
      const data = normalizeActivityEvent(payload);
      if (!data.activity_id) return;
      const { company_id, store_id } = data;
      if (!canSeeForStoreAndCompany(store_id, company_id)) return;

      refetchActivities();

      // The event reaches the whole company; only the alert's recipients (ticket agent,
      // specialist, the priority's notify list) get the pop-up, sound and browser alert.
      if (Array.isArray(payload?.user_ids)) {
        const recipientIds = payload.user_ids.map(Number);
        if (!recipientIds.includes(Number(userInfo?.id))) return;
      }

      // kind: "warning" | "breach" | "auto_closed" (ActivitySlaNotifier::KIND_*).
      const kind = payload?.kind;
      const typeName = typeNameFor(data.type_id);
      const priorityName =
        payload?.priority?.[i18n.language] ?? payload?.priority?.en ?? "";
      const prioritySuffix = priorityName ? ` (${priorityName})` : "";
      const vars = { type: typeName, id: data.activity_id, priority: prioritySuffix };

      let title: string;
      let description: string;
      let show: typeof notification.info;
      if (kind === "auto_closed") {
        title = t("notifications.slaAutoClosed.title", "Ticket closed automatically");
        description = t(
          "notifications.slaAutoClosed.body",
          "{{type}} #{{id}}{{priority}} reached its closing time and was closed automatically",
          vars
        );
        show = notification.info;
      } else if (kind === "breach") {
        title = t("notifications.slaBreach.title", "Closing time passed");
        description = t(
          "notifications.slaBreach.body",
          "{{type}} #{{id}}{{priority}} is still open after its closing time",
          vars
        );
        show = notification.error;
      } else {
        title = t("notifications.slaWarning.title", "Closing time approaching");
        description = t(
          "notifications.slaWarning.body",
          "{{type}} #{{id}}{{priority}} must be closed soon",
          vars
        );
        show = notification.warning;
      }

      show({
        message: title,
        description: activityToastDescription(
          data.activity_id,
          `${typeName} `,
          prioritySuffix
        ),
        duration: SLA_TOAST_SECONDS,
      });
      addNotification({
        kind: "status",
        title,
        body: description,
        path: `/all-activities?activityId=${data.activity_id}`,
        copyId: data.activity_id,
      });
      playFor("status_changed");
      showBrowserNotification(title, description);
    };

    // Order notifications (support multiple backend naming conventions)
    socket.on("newOrder", handleNewOrder);
    socket.on("new_order", handleNewOrder);
    socket.on("orderCreated", handleNewOrder);
    socket.on("createOrder", handleNewOrder);
    socket.on("mobileOrderCreated", handleNewOrder);
    socket.on("newOrderFromApp", handleNewOrder);
    socket.on("orderFulfillment", handleOrderFulfillment);
    socket.on("order_fulfillment", handleOrderFulfillment);
    socket.on("order_missing", handleOrderMissing);
    socket.on("orderMissing", handleOrderMissing);
    socket.on("missingOrder", handleOrderMissing);
    socket.on("changeOrderPay", handleChangeOrderPay);
    socket.on("cancelOrder", handleCancelOrder);
    socket.on("returnOrder", handleReturnOrder);

    // Ticket/activity notifications (support ticket aliases)
    socket.on("newActivity", handleNewActivity);
    socket.on("newTicket", handleNewActivity);
    socket.on("ticketCreated", handleNewActivity);
    socket.on("activityReply", handleActivityReply);
    socket.on("ticketReplied", handleActivityReply);
    socket.on("activityStatusChanged", handleActivityStatusChanged);
    socket.on("ticketUpdated", handleActivityStatusChanged);
    socket.on("editTicket", handleActivityStatusChanged);
    socket.on("activitySlaAlert", handleActivitySlaAlert);

    return () => {
      socket.off("newOrder", handleNewOrder);
      socket.off("new_order", handleNewOrder);
      socket.off("orderCreated", handleNewOrder);
      socket.off("createOrder", handleNewOrder);
      socket.off("mobileOrderCreated", handleNewOrder);
      socket.off("newOrderFromApp", handleNewOrder);
      socket.off("orderFulfillment", handleOrderFulfillment);
      socket.off("order_fulfillment", handleOrderFulfillment);
      socket.off("order_missing", handleOrderMissing);
      socket.off("orderMissing", handleOrderMissing);
      socket.off("missingOrder", handleOrderMissing);
      socket.off("changeOrderPay", handleChangeOrderPay);
      socket.off("cancelOrder", handleCancelOrder);
      socket.off("returnOrder", handleReturnOrder);
      socket.off("newActivity", handleNewActivity);
      socket.off("newTicket", handleNewActivity);
      socket.off("ticketCreated", handleNewActivity);
      socket.off("activityReply", handleActivityReply);
      socket.off("ticketReplied", handleActivityReply);
      socket.off("activityStatusChanged", handleActivityStatusChanged);
      socket.off("activitySlaAlert", handleActivitySlaAlert);
      socket.off("ticketUpdated", handleActivityStatusChanged);
      socket.off("editTicket", handleActivityStatusChanged);
    };
  }, [socket, userInfo, company, sounds, queryClient, t, history, addNotification]);

  return null;
};


